import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Loader2 } from "lucide-react";
import { PageHeader, KV } from "@/pages/Accounts";

export default function MessagesPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [acctId, setAcctId] = useState("");
  const [phone, setPhone] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        if (user?.role === "admin") {
          const { data } = await api.get("/accounts");
          setAccounts(data);
          if (data?.length) setAcctId(data[0].id);
        } else {
          // Analyst — derive accounts from projects they can see
          const { data } = await api.get("/projects");
          const uniq = [...new Map(data.map(p => [p.account_id, { id: p.account_id, name: p.account_name || p.account_id }])).values()];
          setAccounts(uniq);
          if (uniq.length) setAcctId(uniq[0].id);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [user?.role]);

  const search = async () => {
    if (!acctId || phone.trim().length < 3) {
      toast.error("Enter at least 3 digits");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get(
        `/accounts/${acctId}/messages/search`,
        { params: { phone: phone.trim(), limit: 100 } },
      );
      setItems(data.items || []);
      if (!data.items?.length) toast.info("No messages found for this phone");
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    } finally {
      setBusy(false);
    }
  };

  const openDetail = async (messageId) => {
    try {
      const { data } = await api.get(`/accounts/${acctId}/messages/${messageId}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Message Explorer"
        description="Search delivery history across all campaigns for a phone number. Answers 'why did I get this SMS?' in one click."
      />

      <Card data-testid="msg-search-card">
        <CardHeader>
          <CardTitle className="text-base">Search</CardTitle>
          <CardDescription>Enter partial phone (min 3 digits). Cross-campaign lookup.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[220px]">
            <label className="text-xs text-muted-foreground">Account</label>
            <Select value={acctId} onValueChange={setAcctId}>
              <SelectTrigger data-testid="msg-account-select"><SelectValue placeholder="Pick account" /></SelectTrigger>
              <SelectContent>
                {accounts.map(a => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-muted-foreground">Phone (partial or full)</label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="9876543210 or +9198..."
              data-testid="msg-phone-input"
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
          </div>
          <Button onClick={search} disabled={busy || !acctId} data-testid="msg-search-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Search
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="msg-results-card">
        <CardHeader>
          <CardTitle className="text-base">Results</CardTitle>
          <CardDescription>{items === null ? "Enter a phone to search." : `${items.length} message(s)`}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Phone</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>DLT tpl</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Attempted</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items === null && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-xs text-muted-foreground">Search to see results.</TableCell></TableRow>
              )}
              {items?.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-xs text-muted-foreground">No matches.</TableCell></TableRow>
              )}
              {items?.map(m => (
                <TableRow key={m.message_id} data-testid={`msg-row-${m.message_id}`}>
                  <TableCell className="font-mono text-xs">{m.phone_e164}</TableCell>
                  <TableCell className="text-xs">
                    <Link to={`/campaigns/${m.campaign_id}`} className="hover:underline">
                      {m.campaign_name || m.campaign_id.slice(-8)}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">{m.template_name || m.dlt_template_id?.slice(-8) || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={m.status === "accepted" ? "default" : m.status === "failed" ? "destructive" : "secondary"} className="text-[10px]">{m.status}</Badge>
                  </TableCell>
                  <TableCell>
                    {m.delivery_status ? (
                      <Badge variant={m.delivery_status === "delivered" ? "default" : m.delivery_status === "failed" ? "destructive" : "outline"} className="text-[10px]">{m.delivery_status}</Badge>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">{(m.sent_at || m.attempted_at)?.slice(0, 16).replace("T", " ") || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openDetail(m.message_id)} data-testid={`msg-open-${m.message_id}`}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="msg-detail-dialog">
          <DialogHeader>
            <DialogTitle>Message detail</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="text-sm space-y-1">
              <KV k="Message ID" v={<span className="font-mono text-xs">{detail.message_id}</span>} />
              <KV k="Phone" v={<span className="font-mono">{detail.phone_e164}</span>} />
              <KV k="Campaign" v={<Link className="hover:underline" to={`/campaigns/${detail.campaign_id}`}>{detail.campaign_name || detail.campaign_id}</Link>} />
              <KV k="Send status" v={<Badge variant={detail.status === "accepted" ? "default" : "destructive"}>{detail.status}</Badge>} />
              <KV k="Delivery" v={detail.delivery_status ? <Badge variant={detail.delivery_status === "delivered" ? "default" : "destructive"}>{detail.delivery_status}</Badge> : "—"} />
              <KV k="Delivery detail" v={<span className="text-xs">{detail.delivery_status_detail || "—"}</span>} />
              <KV k="KAPSYSTEM msgid" v={<span className="font-mono text-xs">{detail.kapsystem_message_id || "—"}</span>} />
              <KV k="Attempted at" v={<span className="font-mono text-xs">{detail.attempted_at || "—"}</span>} />
              <KV k="Delivered at" v={<span className="font-mono text-xs">{detail.delivered_at || "—"}</span>} />
              {detail.error && <KV k="Error" v={<span className="text-xs text-destructive">{detail.error}</span>} />}
              <div className="pt-2">
                <div className="text-xs text-muted-foreground mb-1">Personalized body</div>
                <pre className="text-xs bg-muted p-3 rounded whitespace-pre-wrap break-words">{detail.personalized_body || "—"}</pre>
              </div>
              {detail.contact_row_snapshot && Object.keys(detail.contact_row_snapshot).length > 0 && (
                <div className="pt-2">
                  <div className="text-xs text-muted-foreground mb-1">Contact row snapshot</div>
                  <pre className="text-xs bg-muted p-3 rounded whitespace-pre-wrap max-h-40 overflow-auto">{JSON.stringify(detail.contact_row_snapshot, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
